import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { hasOpenOverlay, isEditableTarget, topOpenModal } from "../lib/overlay";

/**
 * Бутонът „назад" (Android, жестът на iPhone, стрелката на браузъра) = Esc.
 *
 * Без това „назад" вървеше по историята на браузъра, а в нея почти няма
 * нищо: входът и пренасочванията заместват адреса, модалите и „маси →
 * продукти" не са в нея. Две-три натискания прескачаха модала и страниците
 * и затваряха PWA-то (в браузъра — таба).
 *
 * Сега всяко „назад" натиска Esc — същата стълбица като от клавиатурата
 * (модал → продукти/маси → „Модули" → ПИН, docs/auth-access.md §Изход).
 * Където Esc не прави нищо (личен акаунт на „Модули", мониторът, входът),
 * „назад" също не прави нищо — приложението не се затваря.
 *
 * Изключение — „Справки" и „Склад" (HISTORY_BACK_MODULES): там с отворен
 * модал/попъп „назад" пак е Esc (затваря го), а без него връща предишната
 * страница като досега — друга справка, друг таб, „Модули". Няма ли
 * предишна страница (отворено направо, след презареждане) — Esc.
 *
 * Как: под текущия запис в историята винаги има още един. „Назад" слиза на
 * него, React Router НЕ вижда това (popstate се спира), `history.go` връща
 * на същия запис и чак тогава се натиска Esc — така навигацията на Esc
 * (replace) пише върху правилния запис, а не върху слезлия.
 *
 * Записът се добавя веднъж на зареждане на страницата, при първото
 * докосване: Chrome прескача при „назад" записи, добавени без действие на
 * потребителя. Добавя се ДОРИ да има записи отдолу — след презареждане (F5,
 * нова версия) те са на старата страница и „назад" към тях я зарежда наново,
 * без popstate. После броят записи не расте — връщаме се с `go`, не с нов
 * push. Преди първото докосване „назад" работи постарому.
 * Монтира се веднъж, под router-а (main.tsx).
 */
export default function BackButtonAsEscape() {
  const location = useLocation();
  // Позицията на екрана в историята — към нея се връщаме след всяко „назад".
  const currentIndexRef = useRef<number | null>(null);
  const currentPathnameRef = useRef(location.pathname);
  // Страницата (адрес + ?параметри) на всяка позиция, записана от ТАЗИ
  // страница — по-старите (преди презареждане) не се знаят.
  const pagesRef = useRef(new Map<number, string>());
  // „Назад" е хванато и чакаме връщането, за да натиснем Esc.
  const returningRef = useRef(false);
  // Позицията, до която прескачаме към предишната страница — там popstate-ът
  // е на React Router.
  const passThroughIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const index = readHistoryIndex(window.history.state);
    currentIndexRef.current = index;
    currentPathnameRef.current = location.pathname;
    if (index === null) return;
    const pages = pagesRef.current;
    // Записите над текущия са изхвърлени (push) или са „напред" — не трябват.
    for (const known of [...pages.keys()]) if (known > index) pages.delete(known);
    pages.set(index, location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    const options = { capture: true };
    const removeListeners = () => {
      window.removeEventListener("click", addEntryBelow, options);
      window.removeEventListener("keydown", addEntryBelow, options);
    };
    // capture — преди клик, който сам навигира (push-ът му идва след нашия).
    function addEntryBelow() {
      removeListeners();
      const state = window.history.state as Record<string, unknown> | null;
      const index = readHistoryIndex(state);
      if (index === null) return;
      // Копие на записа на React Router: същият екран (key, usr), позиция +1.
      window.history.pushState({ ...state, idx: index + 1 }, "");
      currentIndexRef.current = index + 1;
      const page = pagesRef.current.get(index);
      if (page !== undefined) pagesRef.current.set(index + 1, page);
    }
    window.addEventListener("click", addEntryBelow, options);
    window.addEventListener("keydown", addEntryBelow, options);
    return removeListeners;
  }, []);

  // Layout, не обикновен effect: listener-ът ТРЯБВА да е закачен преди
  // този на React Router (BrowserRouter го закача в своя layout effect, а
  // този на детето минава преди родителя). Иначе React Router обработва
  // „назад" пръв — показва стария екран и пренаписва позицията. `capture`
  // не помага: Chrome вика listener-ите на window по реда на закачане.
  useLayoutEffect(() => {
    // Предишната страница за „назад" в „Справки"/„Склад" без отворен слой;
    // null — „назад" е Esc. DOM-ът още е на текущия екран.
    const previousPageFor = (landedIndex: number) => {
      if (!isHistoryBackPage(currentPathnameRef.current) || hasOpenOverlay())
        return null;
      const current = pagesRef.current.get(currentIndexRef.current ?? -1);
      if (current === undefined) return null;
      return findPreviousPage(pagesRef.current, landedIndex, current);
    };
    const onPopState = (event: PopStateEvent) => {
      const landedIndex = readHistoryIndex(event.state);
      const currentIndex = currentIndexRef.current;
      if (landedIndex === null || currentIndex === null) return;
      if (landedIndex === passThroughIndexRef.current) {
        // Копията са прескочени — React Router показва предишната страница.
        passThroughIndexRef.current = null;
        return;
      }
      if (landedIndex < currentIndex) {
        const previousPage = previousPageFor(landedIndex);
        if (previousPage === landedIndex) return; // React Router я показва
        event.stopImmediatePropagation();
        if (previousPage !== null) {
          // Под нас е копие на същата страница — слизаме още до предишната.
          passThroughIndexRef.current = previousPage;
          window.history.go(previousPage - landedIndex);
          return;
        }
        // „Назад" = Esc: старият екран не се показва — връщаме се на същия запис.
        returningRef.current = true;
        window.history.go(currentIndex - landedIndex);
        return;
      }
      if (landedIndex === currentIndex && returningRef.current) {
        event.stopImmediatePropagation();
        returningRef.current = false;
        pressEscape();
      }
      // „Напред" (стрелката на браузъра) остава на React Router.
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return null;
}

// Модулите, в които „назад" без отворен модал/попъп връща предишната
// страница, както по историята на браузъра (изрично искане).
const HISTORY_BACK_MODULES = ["/reports", "/inventory"];

function isHistoryBackPage(pathname: string) {
  return HISTORY_BACK_MODULES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

// Най-близката позиция от `fromIndex` надолу с друга страница; null —
// стигаме до непознат запис (преди презареждане) или до дъното.
function findPreviousPage(
  pages: Map<number, string>,
  fromIndex: number,
  currentPage: string,
): number | null {
  for (let index = fromIndex; index >= 0; index--) {
    const page = pages.get(index);
    if (page === undefined) return null;
    if (page !== currentPage) return index;
  }
  return null;
}

// Позицията на записа в историята — React Router я пише в `history.state.idx`
// (0 за първия запис на таба, +1 при push, същата при replace).
function readHistoryIndex(state: unknown): number | null {
  const index = (state as { idx?: unknown } | null)?.idx;
  return typeof index === "number" ? index : null;
}

// Esc както от клавиатурата: тръгва от фокуса (там слушат модалите и
// търсачките) и стига до window (глобалните стъпки „назад").
function pressEscape() {
  const focused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : document.body;
  // Фокус извън отворения модал (тап по празно) — Esc от клавиатурата не би
  // го затворил, а „назад" трябва.
  const modal = topOpenModal();
  const target = modal && !modal.contains(focused) ? modal : focused;
  if (dispatchEscape(target) || !isEditableTarget(target) || hasOpenOverlay())
    return;
  // Фокус в поле и никой не пое Esc. От клавиатурата Esc там не навигира,
  // но на телефона полето остава на фокус и след скриване на клавиатурата —
  // „назад" би заседнало. Затова: Esc още веднъж, вече извън полето.
  target.blur();
  dispatchEscape(document.body);
}

function dispatchEscape(target: HTMLElement) {
  const event = new KeyboardEvent("keydown", {
    key: "Escape",
    code: "Escape",
    // rc-dialog (antd модалите) гледа keyCode, не key.
    keyCode: 27,
    bubbles: true,
    cancelable: true,
  });
  target.dispatchEvent(event);
  return event.defaultPrevented;
}
